// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// ** Axios Imports
import axios from 'axios'
import dayjs from 'dayjs'
import { getServiceRequests, getWorkRequirements } from 'screens/DispatchBoard/logic/DispatchBoardLogic'
import { saveJob } from 'screens/WorkOrders/logic/WorkRequirementScheduleLogic'
import { toast } from 'react-toastify'

// ** Fetch Events
// export const fetchEvents = createAsyncThunk('appCalendar/fetchEvents', async calendars => {
//   const response = await axios.get('/apps/calendar/events', {
//     params: {
//       calendars
//     }
//   })

//   return response.data
// })


export const fetchEvents = createAsyncThunk(
  'appCalendar/fetchEvents',
  async (_, { rejectWithValue }) => {
    try {
      // 1) grab all SRs & WRs
      const [svcReqs, wrs] = await Promise.all([
        getServiceRequests(),    // returns array of { requestID, entityType, customerName, requestTitle… }
        getWorkRequirements()    // returns array of { workReqID, entityID, subServiceName, jobs: […] }
      ])

      const events = []

      // 2) WORK ORDER EVENTS (occurrenceNo > 0)
      svcReqs
      .filter(sr => sr.entityType === 'WorkOrder')
      .forEach(sr => {
        wrs
          .filter(wr => wr.entityID === sr.requestID)
          .forEach(wr => {
            (wr.jobs || [])
              .filter(job => job.occurrenceNo > 0 && job.scheduleStartDate)
              .forEach(job => {

                const assigneeIds = Array.isArray(job.assignees)
                ? job.assignees.map(a => a.employeeID)
                : [];
                
                // Combine date and time for calendar display
                let startDateTime = job.scheduleStartDate;
                let endDateTime = job.scheduleEndDate;
                
                if (job.scheduleStartDate && job.scheduleStartTime) {
                  const dateStr = dayjs(job.scheduleStartDate).format('YYYY-MM-DD');
                  startDateTime = `${dateStr}T${job.scheduleStartTime}`;
                }
                
                if (job.scheduleEndDate && job.scheduleEndTime) {
                  const dateStr = dayjs(job.scheduleEndDate).format('YYYY-MM-DD');
                  endDateTime = `${dateStr}T${job.scheduleEndTime}`;
                }
                
                events.push({
                  id: `${sr.requestID}-${wr.workReqID}-${job.occurrenceNo}`,
                  title: `${sr.customerName} – ${wr.subServiceName}`,
                  start: startDateTime,
                  end: endDateTime,
                  allDay: false,
                  extendedProps: {
                    requestTitle:   sr.requestTitle,
                    calendar:       'Job',
                    requestID:      sr.requestID,
                    workReqID:      wr.workReqID,
                    jobID:         job.jobID,
                    occurrenceNo:   job.occurrenceNo,
                    assignees:      assigneeIds,
                    // <<< new props >>>
                    customerID:     sr.customerID,
                    customerName:   sr.customerName,
                    jobSiteID:      sr.jobSiteID,
                    jobSiteName:    sr.jobSiteName,
                    subServiceID:   wr.subServiceID,
                    subServiceName: wr.subServiceName,
                    industryName:   wr.industryName || null,  // Add industryName
                    industryID:     wr.industryID || null,    // Add industryID
                    status:         job.status || null,        // Add job status
                    description:    sr.reqDescription,
                    specialInstructions: wr.specialInstructions || []
                  }
                })
              })
          })
      })

      // 3) ASSESSMENT EVENTS (occurrenceNo == 0)
      svcReqs
      .filter(sr => sr.entityType === 'Assessment')
      .forEach(sr => {
        // find the single WR with subServiceID=0
        const wr = wrs.find(
          w => w.entityID === sr.requestID && w.subServiceID === 0
        )
        if (!wr) return
    
        // find the scheduled "job" (occurrenceNo===0)
        const job0 = (wr.jobs || []).find(
          j => j.occurrenceNo === 0 && j.scheduleStartDate
        )
        if (!job0) return
    
        // normalize assignees to ID[]
        const assigneeIds = Array.isArray(job0.assignees)
          ? job0.assignees.map(a => a.employeeID)
          : []
    
        // carry through any specialInstructions objects
        const insts = wr.specialInstructions || []
        
        // Combine date and time for calendar display
        let startDateTime = job0.scheduleStartDate;
        let endDateTime = job0.scheduleEndDate;
        
        if (job0.scheduleStartDate && job0.scheduleStartTime) {
          const dateStr = dayjs(job0.scheduleStartDate).format('YYYY-MM-DD');
          startDateTime = `${dateStr}T${job0.scheduleStartTime}`;
        }
        
        if (job0.scheduleEndDate && job0.scheduleEndTime) {
          const dateStr = dayjs(job0.scheduleEndDate).format('YYYY-MM-DD');
          endDateTime = `${dateStr}T${job0.scheduleEndTime}`;
        }
    
        events.push({
          id: `AST-${sr.requestID}`,
          title: sr.requestTitle,
          start: startDateTime,
          end: endDateTime,
          allDay: false,
          extendedProps: {
            requestTitle:        sr.requestTitle,
            calendar:            'Assessment',
            requestID:           sr.requestID,
            workReqID:           wr.workReqID,
            jobID:               job0.jobID,
            occurrenceNo:        job0.occurrenceNo,
            assignees:           assigneeIds,
            customerID:          sr.customerID,
            customerName:        sr.customerName,
            jobSiteID:           sr.jobSiteID,
            jobSiteName:         sr.jobSiteName,
            subServiceID:        wr.subServiceID,
            subServiceName:      wr.subServiceName,
            industryName:        wr.industryName || null,  // Add industryName
            industryID:          wr.industryID || null,    // Add industryID
            status:              sr.status || null,         // Use Service Request status for assessments (not job status)
            description:         sr.reqDescription,
            specialInstructions: insts
          }
        })
      })

      return events
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// ** Add Event
export const addEvent = createAsyncThunk('appCalendar/addEvent', async (event, { dispatch }) => {
  const response = await axios.post('/apps/calendar/add-event', {
    data: {
      event
    }
  })
  await dispatch(fetchEvents(['Job', 'Assessment']))

  return response.data.event
})

// ** Update Event
// export const updateEvent = createAsyncThunk('appCalendar/updateEvent', async (event, { dispatch }) => {
//   const response = await axios.post('/apps/calendar/update-event', {
//     data: {
//       event
//     }
//   })
//   await dispatch(fetchEvents(['Job', 'Assessment']))

//   return response.data.event
// })

export const updateEvent = createAsyncThunk(
  'appCalendar/updateEvent',
  async (plainEvent, { dispatch, rejectWithValue }) => {
    try {
      // 1) fetch the full WR record so we can grab all its job fields
      const allWRs = await getWorkRequirements(/* navigate if needed */)
      const wr = allWRs.find(
        w => w.workReqID === plainEvent.extendedProps.workReqID
      )
      if (!wr) throw new Error(`WR ${plainEvent.extendedProps.workReqID} not found`)

      // 2) find the exact job inside that WR
      const jobID   = plainEvent.extendedProps.jobID
      const origJob = wr.jobs.find(j => j.jobID === jobID)
      if (!origJob) throw new Error(`Job ${jobID} not found in WR ${wr.workReqID}`)

      // 3) build your single-job payload, copying every field except the two datetimes
      const updatedJobPayload = {
        jobID:          origJob.jobID,
        workReqID:      wr.workReqID,
        flatRate:       origJob.flatRate,
        billingType:    origJob.billingType,
        occurrenceNo:   origJob.occurrenceNo,
        scheduleStartDate: dayjs(plainEvent.start).format("YYYY-MM-DD"),
        scheduleStartTime: dayjs(plainEvent.start).format("HH:mm:ss"),
        scheduleEndDate: dayjs(plainEvent.end).format("YYYY-MM-DD"),
        scheduleEndTime: dayjs(plainEvent.end).format("HH:mm:ss"),
        scheduleMethod: origJob.scheduleMethod,
        reminder:       origJob.reminder,
        // reminderTime:   origJob.reminderTime,
        // assignees:      origJob.assignees || null,      // array of IDs
        status:         origJob.status          // if your model has it
      }
      // 4) call your saveJob endpoint instead of saveWorkRequirementSchedule
      const result = await saveJob(updatedJobPayload)
      if (!result || result.isError) {
        throw new Error(result?.message || 'Job update failed')
      }

      toast.success(`Job schedule updated successfully!`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
         theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });

      // 5) reload the calendar
      await dispatch(fetchEvents())

      return plainEvent
    } catch (err) {
      console.error("Could not update job on drag/resize:", err)
      
      return rejectWithValue(err.message)
    }
  }
)


// ** Delete Event
export const deleteEvent = createAsyncThunk('appCalendar/deleteEvent', async (id, { dispatch }) => {
  const response = await axios.delete('/apps/calendar/remove-event', {
    params: { id }
  })
  await dispatch(fetchEvents(['Job', 'Assessment']))

  return response.data
})

// export const appCalendarSlice = createSlice({
//   name: 'appCalendar',
//   initialState: {
//     events: [],
//     selectedEvent: null,
//     selectedCalendars: ['Job', 'Assessment']
//   },
//   reducers: {
//     handleSelectEvent: (state, action) => {
//       state.selectedEvent = action.payload
//     },
//     handleCalendarsUpdate: (state, action) => {
//       const filterIndex = state.selectedCalendars.findIndex(i => i === action.payload)
//       if (state.selectedCalendars.includes(action.payload)) {
//         state.selectedCalendars.splice(filterIndex, 1)
//       } else {
//         state.selectedCalendars.push(action.payload)
//       }
//       if (state.selectedCalendars.length === 0) {
//         state.events.length = 0
//       }
//     },
//     handleAllCalendars: (state, action) => {
//       const value = action.payload
//       if (value === true) {
//         state.selectedCalendars = ['Job', 'Assessment']
//       } else {
//         state.selectedCalendars = []
//       }
//     }
//   },
//   extraReducers: builder => {
//     builder.addCase(fetchEvents.fulfilled, (state, action) => {
//       state.events = action.payload
//     })
//   }
// })

export const appCalendarSlice = createSlice({
  name: 'appCalendar',
  initialState: {
    events: [],
    selectedEvent: null,
    selectedCalendars: ['Job','Assessment']
  },
  reducers: {
    handleSelectEvent: (state, action) => { state.selectedEvent = action.payload },
    handleCalendarsUpdate: (state, action) => {
    const filterIndex = state.selectedCalendars.findIndex(i => i === action.payload)
      if (state.selectedCalendars.includes(action.payload)) {
        state.selectedCalendars.splice(filterIndex, 1)
      } else {
        state.selectedCalendars.push(action.payload)
      }
      if (state.selectedCalendars.length === 0) {
        state.events.length = 0
      }
     },
    handleAllCalendars:    (state, action) => { 
      const value = action.payload
        if (value === true) {
          state.selectedCalendars = ['Job', 'Assessment']
        } else {
          state.selectedCalendars = []
        }
     }
  },
  extraReducers: builder => {
    builder.addCase(fetchEvents.fulfilled, (state, action) => {
      state.events = action.payload
    })
  }
})

export const { handleSelectEvent, handleCalendarsUpdate, handleAllCalendars } = appCalendarSlice.actions

export default appCalendarSlice.reducer
